import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Person from '@material-ui/icons/Person';
import Group from '@material-ui/icons/Group';
import InputAdornment from '@mui/material/InputAdornment';
import { Box } from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

interface OwnerEntity {
  kind: string;
  metadata: {
    name: string;
    title?: string;
  };
  spec?: {
    profile?: {
      displayName?: string;
      email?: string;
    };
  };
}

interface OwnerOption {
  value: string;
  label: string;
  kind: string;
}

export const CustomOwnerField = ({
  onChange,
  required,
  formData,
  schema,
  rawErrors,
}: FieldExtensionComponentProps<string>) => {
  const [availableOwners, setAvailableOwners] = useState<OwnerEntity[]>([]);
  const [inputValue, setInputValue] = useState(formData || '');
  const [isOpen, setIsOpen] = useState(false);
  const catalogApi = useApi(catalogApiRef);

  const { title, description, placeholder, helperText } = schema;

  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const [users, groups] = await Promise.all([
            catalogApi.getEntities({ filter: { kind: 'Group' } }),
          catalogApi.getEntities({ filter: { kind: 'User' } }),
        ]);
        setAvailableOwners([...users.items, ...groups.items] as OwnerEntity[]);
      } catch (error) {
        console.error('Failed to fetch owners:', error);
      }
    };
    fetchOwners();
  }, [catalogApi]);

  const ownerOptions: OwnerOption[] = useMemo(
    () =>
      availableOwners.map(owner => ({
        value: `${owner.kind.toLowerCase()}:default/${owner.metadata.name}`,
        label: owner.spec?.profile?.displayName || owner.metadata.title || owner.metadata.name,
        kind: owner.kind,
      })),
    [availableOwners],
  );

  const filteredOptions = useMemo(() => {
    if (!inputValue || inputValue.trim().length === 0) return ownerOptions;
    const searchTerm = inputValue.toLowerCase();
    return ownerOptions.filter(
      option =>
        option.label.toLowerCase().includes(searchTerm) ||
        option.value.toLowerCase().includes(searchTerm),
    );
  }, [ownerOptions, inputValue]);

  const hasFormValidationErrors = rawErrors && rawErrors.length > 0;
  const displayError = hasFormValidationErrors ? '' : helperText || description;

  return (
    <Autocomplete
      options={filteredOptions}
      value={ownerOptions.find(option => option.value === formData) || null}
      inputValue={inputValue}
      open={isOpen && (inputValue.length === 0 || filteredOptions.length > 0)}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      onInputChange={(_, newInputValue: string) => {
        setInputValue(newInputValue);
      }}
      onChange={(_, newValue: OwnerOption | null) => {
        if (newValue) {
          setInputValue(newValue.label);
          onChange(newValue.value);
          setIsOpen(false);
        }
      }}
      renderInput={(params: any) => (
        <TextField
          {...params}
          fullWidth
          label={title}
          error={hasFormValidationErrors}
          helperText={displayError}
          placeholder={placeholder}
          required={required}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                {formData?.startsWith('group:') ? <Group /> : <Person />}
              </InputAdornment>
            ),
          }}
        />
      )}
      getOptionLabel={(option: OwnerOption | string) =>
        typeof option === 'string' ? option : option.label
      }
      renderOption={(props: any, option: OwnerOption) => (
        <Box component="li" {...props} display="flex" alignItems="center" style={{ gap: '8px' }}>
          {option.kind === 'Group' ? <Group /> : <Person />}
          {option.label}
        </Box>
      )}
      isOptionEqualToValue={(option: OwnerOption, value: OwnerOption) =>
        option.value === value.value
      }
      noOptionsText=""
      filterOptions={options => options}
      freeSolo={false}
      selectOnFocus
      clearOnBlur={false}
      autoComplete
      autoHighlight
    />
  );
};
